import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandingHeader from './components/BrandingHeader';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import AuthBrandingPanel from '../../components/ui/AuthBrandingPanel';
import { useAuth } from '../../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading } = useAuth();

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const role = typeof user?.role === 'string' ? user.role.toLowerCase() : '';

    if (role === 'employee') {
      navigate('/employee-self-service', { replace: true });
      return;
    }

    if (role === 'manager' || role === 'admin') {
      navigate('/manager-self-service', { replace: true });
      return;
    }

    navigate('/employee-scheduling', { replace: true });
  }, [isAuthenticated, user, navigate, loading]);

  return (
    <div className="auth-theme min-h-screen bg-background">
      {/* Main Container */}
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Login Side */}
        <div className="w-full lg:w-1/2 bg-card p-8 lg:p-12 flex flex-col items-center min-h-screen lg:min-h-0 relative">
          <div className="flex-1 flex flex-col items-center justify-center w-full pt-8 pb-40 lg:pt-0 lg:pb-0 overflow-y-auto">
            <div className="w-full max-w-md">
              {/* Mobile Branding Header */}
              <div className="lg:hidden mb-8">
                <BrandingHeader />
              </div>

              {/* Desktop Login Header */}
              <div className="hidden lg:block text-center mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Welcome Back
                </h2>
                <p className="text-muted-foreground">
                  Sign in to access your restaurant management dashboard
                </p>
              </div>

              {/* Login Form */}
              <LoginForm />
            </div>

            <div className="hidden lg:block w-full max-w-md mx-auto mt-10 space-y-8">
              <SecurityBadges />
              <div className="text-center pt-6 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  © {new Date()?.getFullYear()} Ougama. All rights reserved.
                </p>
                <div className="flex items-center justify-center space-x-4 mt-2">
                  <a href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </a>
                  <span className="text-muted-foreground">•</span>
                  <a href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </a>
                  <span className="text-muted-foreground">•</span>
                  <a href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Support
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:hidden fixed bottom-0 inset-x-0 px-6 pb-6 pt-4 bg-card/95 z-10">
            <div className="w-full max-w-md mx-auto flex flex-col items-center space-y-3">
              <SecurityBadges className="hidden md:flex w-full" />
              <div className="text-center text-xs text-muted-foreground">
                <p>© {new Date()?.getFullYear()} Ougama. All rights reserved.</p>
                <div className="flex items-center justify-center space-x-4 mt-2">
                  <a href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </a>
                  <span className="text-muted-foreground">•</span>
                  <a href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </a>
                  <span className="text-muted-foreground">•</span>
                  <a href="#" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Branding and Information (Desktop only) */}
        <AuthBrandingPanel>
          <p className="text-white/70 text-lg">
            Streamline your restaurant operations with comprehensive workforce management.
          </p>
          <div className="grid gap-4 w-full text-left">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="text-white font-semibold">Smart Scheduling</h3>
              <p className="text-white/70 text-sm">
                Drag-and-drop shift management with real-time availability tracking.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="text-white font-semibold">Team Communication</h3>
              <p className="text-white/70 text-sm">
                Internal messaging system for seamless staff coordination.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="text-white font-semibold">Multi-Location Support</h3>
              <p className="text-white/70 text-sm">
                Manage multiple restaurant locations from a single dashboard.
              </p>
            </div>
          </div>
        </AuthBrandingPanel>
      </div>
    </div>
  );
};

export default LoginPage;
